import { useCallback, useEffect, type PropsWithChildren } from 'react';

import { Spin } from 'antd';

import { useGetCasdoorAuthDataMutation } from '@/api/endpoints/casdoorAuth';
import { useGetTeachbaseAuthDataMutation } from '@/api/endpoints/teachbase';
import { ENVS } from '@/shared/consts';
import {
  getCasdoorAccessToken,
  getPublicSiteUrl,
  getSSOUrlToRedirect,
  getTeachbaseAccessToken,
  saveCasdoorTokens,
  saveTeachbaseToken,
} from '@/shared/helpers';

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [fetchCasdoorAuthData] = useGetCasdoorAuthDataMutation();
  const [fetchTeachbaseAuthData] = useGetTeachbaseAuthDataMutation();

  const casdoorAccessToken = getCasdoorAccessToken();
  const teachbaseAccessToken = getTeachbaseAccessToken();

  const searchParams = new URLSearchParams(window.location.search);

  const authorizationCode = searchParams.get('authorization_code') || '';
  const codeVerifier = searchParams.get('code_verifier') || '';

  const shouldNavigateToSSO = Boolean(!casdoorAccessToken && (!codeVerifier || !authorizationCode));
  const shouldFetchTokens = Boolean(authorizationCode && codeVerifier);

  const getAuthData = useCallback(async () => {
    try {
      const { access_token: accessToken, refresh_token: refreshToken } = await fetchCasdoorAuthData({
        authorizationCode,
        codeVerifier,
      }).unwrap();

      saveCasdoorTokens({ accessToken, refreshToken });

      const { access_token: teachBaseAccessToken } = await fetchTeachbaseAuthData().unwrap();

      saveTeachbaseToken({ accessToken: teachBaseAccessToken });

      window.location.replace(`${window.location.origin}${window.location.pathname}`);
    } catch {
      window.location.href = getPublicSiteUrl();
    }
  }, [fetchCasdoorAuthData, fetchTeachbaseAuthData, authorizationCode, codeVerifier]);

  useEffect(() => {
    if (shouldNavigateToSSO && ENVS.MODE === 'mock') {
      saveCasdoorTokens({ accessToken: '123', refreshToken: '321' });
      saveTeachbaseToken({ accessToken: '123' });

      return;
    }

    if (shouldNavigateToSSO) {
      window.location.href = getSSOUrlToRedirect({
        type: 'admin',
        redirectUri: `${window.location.origin}${window.location.pathname}`,
      });
    } else if (shouldFetchTokens) {
      getAuthData();
    }
  }, [getAuthData, shouldNavigateToSSO, shouldFetchTokens]);

  if (casdoorAccessToken && teachbaseAccessToken && (!authorizationCode || !codeVerifier)) {
    return children;
  }

  return (
    <Spin
      fullscreen
      size="large"
    />
  );
};
